import { csrfFetch } from 'javascripts/csrf_token';
import $ from 'jquery';

type Request = Omit<RequestInit, 'body' | 'method'>;
type GetRequest = Request & {
  params?: Record<string, any>;
};
type PostRequest<TBody> = Request & {
  body?: TBody;
};

export async function getJson<TResponse>(
  url: string,
  { params, ...options }: GetRequest = {}
): Promise<TResponse> {
  const fullUrl = buildUrlWithQueryParams(url, params);
  return fetchJson(fullUrl, options);
}

export async function postJson<TResponse, TBody>(
  url: string,
  { body, ...options }: PostRequest<TBody> = {}
): Promise<TResponse> {
  const requestOptions = {
    method: 'POST',
    body: JSON.stringify(body),
    ...options,
  };
  return fetchJson(url, requestOptions);
}

export async function putJson<TResponse, TBody>(
  url: string,
  { body, ...options }: PostRequest<TBody> = {}
): Promise<TResponse> {
  const requestOptions = {
    method: 'PUT',
    body: JSON.stringify(body),
    ...options,
  };
  return fetchJson(url, requestOptions);
}

function buildUrlWithQueryParams(
  url: string,
  queryParams?: Record<string, any>
) {
  if (!queryParams) return url;

  // Currently using jQuery to serialize the query params because it formats them in the way we expect for our API. This includes handling of arrays and nested objects.
  // We could switch to use a newer version of queryString, for example, but we would need to get some tests in place to ensure comparable behavior.
  const params = $.param(queryParams);
  return url + `?${params}`;
}

export async function fetchJson<TResponse>(
  url: string,
  { headers, ...options }: RequestInit
): Promise<TResponse> {
  const response = await csrfFetch(url, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...headers,
    },
    ...options,
  });

  if (!response.ok) {
    throw new Error(`Response status: ${response.status}`);
  }

  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json() as Promise<TResponse>;
  } else {
    return undefined as TResponse;
  }
}
